/**
 * Column Context Menu Component
 * Right-click menu on column labels (expanded + collapsed)
 */

import { useEffect } from 'react';
import { Pencil, ClipboardList, CheckSquare, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ColumnContextMenuProps {
  x: number;
  y: number;
  column: string;
  onEditColumn: () => void;
  onOpenTaskPool: () => void;
  onSelectAllInColumn: () => void;
  onDeleteColumn?: () => void;
  onClose: () => void;
}

export function ColumnContextMenu({ x, y, column: _column, onEditColumn, onOpenTaskPool, onSelectAllInColumn, onDeleteColumn, onClose }: ColumnContextMenuProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleClick = () => onClose();
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => {
      window.addEventListener('click', handleClick);
      window.addEventListener('keydown', handleEscape);
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '8px 12px',
    fontSize: 10,
    color: '#1f2937',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left' as const,
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 1000,
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 4,
        minWidth: 180,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onEditColumn}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <Pencil size={14} style={{ color: '#3b82f6' }} />
        {t('boardv3.contextMenu.editColumn')}
      </button>
      <button
        onClick={onOpenTaskPool}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <ClipboardList size={14} style={{ color: '#8b5cf6' }} />
        {t('boardv3.contextMenu.openTaskPool')}
      </button>
      <button
        onClick={onSelectAllInColumn}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <CheckSquare size={14} style={{ color: '#3b82f6' }} />
        {t('boardv3.contextMenu.selectAll')}
      </button>
      {onDeleteColumn && (
        <>
          <div style={{ height: 1, backgroundColor: '#e5e7eb', margin: '4px 0' }} />
          <button
            onClick={onDeleteColumn}
            style={{ ...buttonStyle, color: '#dc2626' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <Trash2 size={14} style={{ color: '#dc2626' }} />
            {t('boardv3.contextMenu.deleteColumn')}
          </button>
        </>
      )}
    </div>
  );
}
